import { applyPersonPose } from '../entities/person/pose.js';
/**
 * Achievements — banner de CONQUISTA no topo-central da tela.
 *
 * Quando um jogador conclui uma missão/frase, o backend manda (efeito onAchievement)
 * nome + mensagem + pontos; aqui exibimos um card no topo com os AVATARES do jogador
 * (o CARRO atrás da PESSOA, mesma composição/estilo do jogo) + nome + mensagem + pontos.
 * As conquistas entram numa FILA e aparecem uma de cada vez (auto-some após alguns s).
 *
 * O retrato é um mini-renderer WebGL próprio (lazy) que reusa EXATAMENTE as peças e as
 * regras de aparência do jogo (buildCarParts/buildPersonParts + buildAvatarGroup), então
 * o avatar da conquista é idêntico ao que anda pela cidade.
 */
import * as THREE from 'three';
import { buildCarParts } from '../entities/carModel.js';
import { buildPersonParts } from '../entities/personModel.js';
import { buildAvatarGroup, clearAvatarGroups as clearGroups } from '../entities/partsRuntime.js';
import { attachContextRecovery, desistiuDoRestore } from './glRecovery.js';
import { probes, P } from '../perfProbes.js';
import { t } from '../i18n.js';

// peças montadas UMA vez (idênticas ao jogo — primitivas). buildAvatarGroup só seleciona
// e tinge as peças visíveis conforme a aparência de cada jogador. Exportadas p/ o
// rasterizador dos chips de conquista (AvatarPortrait) reusar sem reconstruir.
// 'studio': o retrato do toast mostra UM avatar em close — usa a tesselação cheia
// (o Fleet, com centenas de avatares na vista aérea, usa a de jogo). Ver lod.js.
// Taxa de atualização do retrato 3D do banner (ver update()).
const PORTRAIT_HZ = 30;
const PORTRAIT_DT = 1000 / PORTRAIT_HZ;

export const CAR_PARTS = buildCarParts('studio');
export const PERSON_PARTS = buildPersonParts('studio');

// RITMO ADAPTATIVO: com a fila vazia cada conquista fica SHOW_MS; conforme a fila
// cresce, o tempo de exibição ENCURTA (STEP_MS por item aguardando) até o piso
// MIN_SHOW_MS — o telão celebra todo mundo sem acumular atraso. Acima de QUEUE_MAX
// aguardando, as MAIS ANTIGAS são descartadas (celebrar algo de minutos atrás não
// faz sentido; limita a espera máxima a ~QUEUE_MAX × MIN_SHOW_MS ≈ 48s).
const SHOW_MS = 5200;     // tempo visível com fila vazia
const MIN_SHOW_MS = 2000; // piso (ainda legível no telão) com fila cheia
const STEP_MS = 450;      // encurta isto por conquista aguardando na fila
const QUEUE_MAX = 24;     // teto da fila (estouro descarta as mais antigas)
const HIDE_MS = 380;      // duração da animação de SAÍDA (keyframe ach-out no hud.css)
const JUMP_H = 0.55;      // altura do PULO de celebração do retrato (unidades do avatar)
const AVA_W = 192;    // buffer (lógico) do retrato — 2× o CSS de #ach-canvas (96×84, supersample)
const AVA_H = 168;

export class Achievements {
  constructor() {
    this.el = document.getElementById('achievement');
    this.nameEl = document.getElementById('ach-name');
    this.msgEl = document.getElementById('ach-msg');
    this.ptsEl = document.getElementById('ach-points');
    this.canvas = document.getElementById('ach-canvas');
    this._queue = [];
    this._active = null; // { until }
    this._t = 0;
    this._renderer = null; // lazy (criado no 1º uso)
    this._lost = false;    // contexto WebGL perdido (ver glRecovery.js)
  }

  /** Enfileira uma conquista. item = { name, message, points, carAppearance, personAppearance } */
  show(item) {
    if (!this.el || !item) return;
    this._queue.push(item);
    if (this._queue.length > QUEUE_MAX) this._queue.splice(0, this._queue.length - QUEUE_MAX);
    if (!this._active) { this._next(); return; }
    // fila cresceu → ENCURTA também a conquista ATUAL (rajada não espera os 5,2s dela),
    // respeitando o piso a partir do início dela (nunca corta abaixo de MIN_SHOW_MS).
    const until = this._active.start + this._duration();
    if (until < this._active.until) this._active.until = Math.max(until, this._active.start + MIN_SHOW_MS);
  }

  /** Tempo de exibição conforme a fila: cheia → mais curto (drena mais rápido). */
  _duration() {
    return Math.max(MIN_SHOW_MS, SHOW_MS - this._queue.length * STEP_MS);
  }

  /** Chamado pelo loop principal: anima o retrato, dispara a SAÍDA animada quando a
   *  conquista expira e troca p/ a próxima ao fim da animação de saída. */
  /**
   * @param {number} [chipsDesenharam] renders auxiliares que os CHIPS já fizeram
   * neste quadro (Hud.update). Quando > 0, o retrato do banner adia 1 quadro —
   * banner e chips ficam DEFASADOS e o pico "N renders auxiliares no mesmo
   * quadro" cai. O adiamento é limitado (1,8× o período): o banner nunca fica
   * abaixo de ~16 Hz nem para de animar se os chips desenharem sempre.
   */
  update(chipsDesenharam) {
    if (!this._active) return;
    // tempo RELATIVO ao início desta conquista → o balanço começa SEMPRE da mesma
    // fase (0) — toda exibição do mesmo avatar fica idêntica (determinística).
    this._t = (performance.now() - this._active.start) / 1000;
    // RETRATO A PORTRAIT_HZ. Este é o render auxiliar mais caro do jogo: 36 draw
    // calls e ~32k triângulos num buffer que chega a 384×336. E é SUSTENTADO — com
    // fila cheia (QUEUE_MAX × MIN_SHOW_MS) fica na tela por até ~48 s seguidos, o
    // que a 60 fps dava ~2.180 draw calls por segundo só do banner. A ~30 Hz o
    // balanço (0,9 rad/s) e o pulo continuam fluidos: o passo é de 3 graus.
    const agora = performance.now();
    const vencido = agora - (this._portraitAt || 0);
    if (vencido >= PORTRAIT_DT && !(chipsDesenharam > 0 && vencido < PORTRAIT_DT * 1.8)) {
      this._portraitAt = agora;
      this._renderPortrait();
    }
    const now = performance.now();
    if (this._active.hideAt == null && now >= this._active.until) {
      this._active.hideAt = now; // expira → começa a animação de saída (ach-out)
      this.el.classList.remove('is-show');
      this.el.classList.add('is-hide');
    }
    if (this._active.hideAt != null && now >= this._active.hideAt + HIDE_MS) this._next();
  }

  /* ------------------------------ fila / card ------------------------------ */

  _next() {
    this.el.classList.remove('is-hide');
    const item = this._queue.shift();
    if (!item) { this._active = null; this.el.classList.remove('is-show'); return; }
    const now = performance.now();
    this._active = { start: now, until: now + this._duration() }; // adaptativo à fila
    this.nameEl.textContent = item.name || t('Jogador');
    this.msgEl.textContent = item.message || '';
    this.ptsEl.textContent = String(item.points ?? 0);
    // Conquista que NÃO pontua (o jogador já fez este edifício noutra cidade — ele só
    // pontua 1× por edifício) ainda celebra, mas sem o selo "+0 pts", que só confunde.
    const pts = this.ptsEl.closest('.ach__points');
    if (pts) pts.style.display = Number(item.points) > 0 ? '' : 'none';
    this._setPortrait(item);
    // reinicia a animação de entrada (remove → reflow → readiciona)
    this.el.classList.remove('is-show');
    void this.el.offsetWidth;
    this.el.classList.add('is-show');
  }

  /* --------------------- retrato 3D (carro atrás da pessoa) --------------------- */

  _ensureRenderer() {
    // O restore não veio? Descarta e recomeça com um CANVAS NOVO — um canvas que perdeu o
    // contexto não devolve outro. Sem isto, o flag `_lost` bloqueava `_renderPortrait`
    // para sempre e toda conquista das horas seguintes saía com nome, mensagem e pontos
    // mas com a caixa do avatar VAZIA. Ver AvatarPortrait, que já fazia o mesmo.
    if (this._lost && desistiuDoRestore(this)) {
      try { this._renderer?.dispose(); } catch { /* já morto */ }
      const velho = this.canvas;
      const novo = velho.cloneNode(false);
      velho.parentNode?.replaceChild(novo, velho);
      this.canvas = novo;
      this._renderer = null;
      this._lost = false; this._lostAt = 0;
    }
    if (this._renderer) return;
    const r = new THREE.WebGLRenderer({ canvas: this.canvas, alpha: true, antialias: true });
    const dimensionar = () => {
      r.setPixelRatio(Math.min(window.devicePixelRatio || 1, 2));
      r.setSize(AVA_W, AVA_H, false); // buffer = tamanho*dpr; o CSS controla o display
    };
    dimensionar();
    // perda de contexto WebGL (driver atualizado, suspensão, GPU reset) — num telão de
    // 8 h isso acontece. O three já torna o render() um no-op seguro; o que faltava era
    // o SUBSISTEMA parar (não gastar CPU montando/animando retrato à toa) e voltar
    // limpo no restore. Ver glRecovery.js.
    attachContextRecovery(this.canvas, this, dimensionar);
    this._renderer = r;
    const scene = new THREE.Scene();
    scene.add(new THREE.HemisphereLight(0xffffff, 0x2a3a30, 1.2));
    const dir = new THREE.DirectionalLight(0xffffff, 1.6);
    dir.position.set(2.5, 4, 3);
    scene.add(dir);
    const cam = new THREE.PerspectiveCamera(30, AVA_W / AVA_H, 0.1, 100);
    const inner = new THREE.Group(); // conteúdo centralizado na origem
    const holder = new THREE.Group(); // gira (balanço) em torno do centro
    holder.add(inner);
    scene.add(holder);
    this._scene = scene;
    this._cam = cam;
    this._holder = holder;
    this._inner = inner;
  }

  /**
   * PRÉ-AQUECIMENTO (ocioso, pós-boot): cria o contexto WebGL e compila os shaders
   * ANTES da primeira conquista, que hoje pagava tudo isso no próprio quadro em que
   * o banner aparece. O banner está sem `is-show`, então o render é invisível; o
   * estado que isto deixa em `_inner`/`_person` é integralmente substituído pelo
   * `_setPortrait` da primeira conquista real.
   */
  prewarm(pares) {
    // Conquista REAL chegou antes do idle: o banner ativo já criou o contexto e
    // compilou os shaders — o objetivo do prewarm está pago. Prosseguir aqui
    // SOBRESCREVERIA o retrato em exibição com um avatar sorteado.
    if (this._active) return;
    this._ensureRenderer();
    if (this._lost) return;
    this._t = 0;
    for (const [carApp, personApp] of pares) {
      this._setPortrait({ carAppearance: carApp, personAppearance: personApp });
      this._renderPortrait();
    }
  }

  _setPortrait(item) {
    this._ensureRenderer();
    const inner = this._inner;
    // descarta os materiais CLONADOS do retrato anterior. Antes só removia da cena:
    // cada conquista deixava ~15 clones vivos (o renderer guarda estado por material
    // até o dispose) e num telão de 8 h isso só cresce.
    clearGroups(inner);

    // ZERA os transforms ANTES de medir: `inner.position` ainda tem o deslocamento do
    // retrato ANTERIOR e o `holder` a rotação do balanço (fase aleatória do último
    // frame) — medir com eles "sujos" desloca a caixa → cada exibição saía com
    // enquadramento DIFERENTE (mesmo p/ o mesmo jogador).
    this._holder.rotation.y = 0;
    inner.position.set(0, 0, 0);

    // pessoa (frente, HERÓI) + carro (atrás, cenário), ambos encarando a câmera (+Z).
    // O carro fica MENOR e bem RECUADO: os modelos variam muito de tamanho (Hilux
    // longa, Defender alto) e, perto demais, invadiam o plano da pessoa — o retrato
    // saía "torto" (pessoa engolida/composição fora do centro).
    const person = buildAvatarGroup(PERSON_PARTS, item.personAppearance);
    const car = buildAvatarGroup(CAR_PARTS, item.carAppearance);
    car.scale.setScalar(0.62);
    car.position.set(-0.55, 0, -2.3);
    car.rotation.y = -0.5; // 3/4 atrás
    person.position.set(0.6, 0, 0.4);
    person.rotation.y = 0.1;
    inner.add(car, person);

    // ANIMAÇÃO DE CONQUISTA: guarda a pessoa + as peças articuladas (braços/pernas/
    // pés, marcadas com `userData.part.anim` pelo buildAvatarGroup) — o retrato pula
    // de alegria balançando os braços (ver _celebrate, chamada por frame no render).
    this._person = person;
    this._animMeshes = person.children.filter((m) => m.userData.part && m.userData.part.anim);

    // centraliza e enquadra a COMPOSIÇÃO INTEIRA (não só a pessoa): qualquer modelo
    // de carro fica dentro do quadro e o conjunto sempre alinhado no card.
    this._scene.updateMatrixWorld(true); // matrizes FRESCAS (holder/inner zerados acima)
    const box = new THREE.Box3().setFromObject(inner);
    box.max.y += JUMP_H; // headroom p/ o PULO da celebração (a cabeça não corta no topo)
    const c = new THREE.Vector3(); box.getCenter(c);
    const size = new THREE.Vector3(); box.getSize(size);
    inner.position.set(-c.x, -c.y, -c.z);
    const fov = (this._cam.fov * Math.PI) / 180;
    const fit = Math.max(size.y, size.x / this._cam.aspect) * 1.18; // respiro (não encosta nas bordas do box)
    this._dist = fit / 2 / Math.tan(fov / 2) + size.z / 2;
    this._dist /= 1.05; // zoom de ~5% (aproxima a câmera)
    // desloca a composição ~7px de TELA p/ a ESQUERDA (ajuste fino de layout no card):
    // converte px do canvas (80px CSS de largura) em unidades de mundo na distância da câmera
    const viewW = 2 * this._dist * Math.tan(fov / 2) * this._cam.aspect;
    inner.position.x -= viewW * (7 / 80);
  }

  _renderPortrait() {
    if (this._lost) this._ensureRenderer();                   // dá a chance de recomeçar
    if (this._lost || !this._renderer || !this._dist) return; // sem contexto: não desenha
    probes.begin(P.auxGL);
    // balanço BEM sutil: o pivô é o centro da composição — amplitude alta jogava o
    // carro (afastado do pivô) para fora do quadro nos extremos.
    this._holder.rotation.y = Math.sin(this._t * 0.9) * 0.1;
    this._celebrate(this._t);
    const cam = this._cam;
    cam.position.set(0, this._dist * 0.1, this._dist);
    cam.lookAt(0, 0, 0);
    this._renderer.render(this._scene, cam);
    probes.end(P.auxGL);
  }

  /**
   * Animação de CONQUISTA do retrato: a pessoa PULA de alegria (com squash ao
   * tocar o chão) balançando OS DOIS braços erguidos — as mesmas "alavancas" da
   * postura `celebrate` do jogo (EntityManager/Fleet: swing/waveZ/postureY/squash),
   * aplicadas aqui aos meshes do retrato (marcados com `userData.part.anim`).
   */
  _celebrate(t) {
    const person = this._person;
    if (!person) return;
    const ph = t * 4.5;
    const j = Math.abs(Math.sin(ph));                       // 0 (chão) .. 1 (topo do pulo)
    const swing = Math.sin(ph * 2) * 0.12;                  // perninhas acompanham
    const wave = 1.9 + j * 0.5 + Math.sin(t * 9) * 0.12;    // braços ERGUIDOS balançando
    const sy = 1 - 0.18 * Math.pow(1 - j, 3);               // squash amortece o pouso
    person.scale.setScalar(1);
    person.position.y = j * JUMP_H + applyPersonPose(person, {swing,waveZ:wave,waveZL:wave,squashY:sy});

  }
}
